'use client'

import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { FiFacebook, FiTwitter, FiLinkedin, FiInstagram, FiYoutube, FiPhone, FiMail, FiMapPin, FiHeart, FiCheck } from 'react-icons/fi'
import { siteConfig } from '@/config/site'

const footerLinks = [
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Blog', href: '/blog' },
  { label: 'Community', href: '/community' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Privacy', href: '/privacy' },
]

const socialLinks = [
  { icon: FiFacebook, href: siteConfig.social.facebook, label: 'Facebook' },
  // { icon: FiTwitter, href: siteConfig.social.twitter, label: 'Twitter' },
  // { icon: FiLinkedin, href: siteConfig.social.linkedin, label: 'LinkedIn' },
  { icon: FiInstagram, href: siteConfig.social.instagram, label: 'Instagram' },
  { icon: FiYoutube, href: siteConfig.social.youtube, label: 'YouTube' },
]

export function Footer() {
  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* About */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link href="/" className="inline-flex items-center gap-2 mb-6">
              <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg shadow-teal-500/30 flex-shrink-0">
                <Image src="/images/logo1.jpeg" alt="Call-A-Doctor Logo" width={64} height={64} className="w-full h-full object-cover" />
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Providing comprehensive healthcare services with compassion, clinical excellence and a patient-centered approach. Your health is our priority.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-teal-600 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.href + link.label}>
                  <Link 
                    href={link.href} 
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="font-bold text-lg mb-6">Services</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li>General Practice</li>
              <li>Vaccinations</li>
              <li>Lab Services</li>
              <li>Mental Wellness</li>
              <li>Life Coaching</li>
              <li>Radio Programs</li>
              <li>Telemedicine</li>
              <li>Transformational Speaking</li>
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h4 className="font-bold text-lg mb-6">Contact</h4>
            <div className="space-y-4">
              <a 
                href={`tel:${siteConfig.phone}`}
                className="flex items-center gap-3 text-slate-400 hover:text-teal-400 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <FiPhone className="w-5 h-5" />
                </div>
                <span>{siteConfig.phone}</span>
              </a>
              <a 
                href={`mailto:${siteConfig.email}`}
                className="flex items-center gap-3 text-slate-400 hover:text-teal-400 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <FiMail className="w-5 h-5" />
                </div>
                <span>{siteConfig.email}</span>
              </a>
              <div className="flex items-start gap-3 text-slate-400">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <FiMapPin className="w-5 h-5" />
                </div>
                <span>{siteConfig.address}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom */}
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} {siteConfig.name}. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm flex items-center gap-1">
            Made with <FiHeart className="w-4 h-4 text-rose-500" /> for your health
          </p>
        </div>
      </div>
    </footer>
  )
}
